import {useState} from "react";
import {useGeolocationContext} from "../context/GeolocationContext";

export type GeolocationErrorType = "permission_denied" | "other";

export type GeolocationError = {
    type: GeolocationErrorType;
    message: string;
};

export type Coordinates = {
    lat: number;
    lon: number;
};

type GetGeolocationResult = {
    coordinates: Coordinates | null;
    error: GeolocationError | null;
};

export function useGeolocation() {
    const [isLoading, setIsLoading] = useState(false);
    const geolocationContext = useGeolocationContext();

    const getGeolocation = async (): Promise<GetGeolocationResult> => {
        setIsLoading(true);

        try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 5000,
                    maximumAge: 0,
                });
            });

            setIsLoading(false);
            return {
                coordinates: {
                    lat: position.coords.latitude,
                    lon: position.coords.longitude,
                },
                error: null,
            };
        } catch (err) {
            const isPermissionDenied =
                err instanceof GeolocationPositionError && err.code === err.PERMISSION_DENIED;

            const error: GeolocationError = {
                type: isPermissionDenied ? "permission_denied" : "other",
                message: err instanceof Error ? err.message : "Failed to get location",
            };

            if (isPermissionDenied) {
                geolocationContext.showGeolocationPermissionDeniedModal();
            }

            setIsLoading(false);
            console.error("Error fetching geolocation:", error);
            return {coordinates: null, error};
        }
    };

    return {getGeolocation, isLoading};
}
